import { supabase } from "../lib/supabase";
import { extractFunctionErrorMessage } from "./edgeFunctionErrors";

/**
 * All integrations actions are routed through the single Edge Function.
 * The caller must supply the advertiser_id and an auth token from the session.
 */
async function invoke(action, advertiserId, authToken, extra = {}) {
  if (!supabase) {
    throw new Error("Supabase client is not initialized");
  }
  const { data, error } = await supabase.functions.invoke("integrations", {
    body: { action, advertiser_id: advertiserId, ...extra },
    headers: { Authorization: `Bearer ${authToken}` },
  });
  if (error) {
    throw new Error(await extractFunctionErrorMessage(error));
  }
  return data;
}

/**
 * Returns the advertiser's sales integration connections, newest first.
 * Shape: array of { id, source, external_account_id, status, sync_state,
 *   environment, granted_scopes, sync_cursor, connected_at, last_sync_at,
 *   last_error, created_at, updated_at, theme_embed_enabled,
 *   theme_embed_checked_at }
 */
export async function listConnections(advertiserId, authToken) {
  const data = await invoke("list", advertiserId, authToken);
  return data?.connections ?? [];
}

/**
 * Disconnects one connection: destroys its Vault secret and marks it
 * disconnected. Shape: { ok: boolean }
 */
export async function disconnectConnection(advertiserId, connectionId, authToken) {
  return invoke("disconnect", advertiserId, authToken, { connection_id: connectionId });
}

/**
 * Asks the Edge Function whether the store's "Audiohook Cart Sync" theme embed
 * is on (DEV-2405). The function reads the theme from Shopify only when that is
 * safe and otherwise returns the stored answer.
 * Shape: { theme_embed_enabled: boolean|null, theme_embed_checked_at: string|null }.
 * Rejects on any failure; callers treat that as "keep what the list said".
 */
export async function getThemeEmbedStatus(advertiserId, connectionId, authToken) {
  return invoke("theme-embed-status", advertiserId, authToken, { connection_id: connectionId });
}
